// Shared attachment helpers + the single visual chip used by both
// GenesisPage (createable + deletable) and MemoryPage (read-only).
// Identical shape, identical render, no drift.
//
// The s3Key on each attachment is the stateless slot: dummy string
// today, real S3 object key when the backend is wired. Bytes never
// live on the chip; only the reference does.

import React from 'react';
import { Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';

import { THEME } from '../../pages/enterprise-ai/genesisData';

export const formatBytes = (n) => {
  if (n == null || Number.isNaN(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const iconForFile = (fileType) => {
  const t = String(fileType || '').toLowerCase();
  if (t === 'pdf') return PictureAsPdfIcon;
  if (t === 'xlsx' || t === 'xls' || t === 'csv') return TableChartIcon;
  if (t === 'doc' || t === 'docx') return ArticleIcon;
  if (t === 'png' || t === 'jpg' || t === 'jpeg' || t === 'gif' || t === 'webp') return ImageIcon;
  if (t === 'txt' || t === 'md') return DescriptionIcon;
  return InsertDriveFileIcon;
};

export const AttachmentChip = ({ attachment, onDelete }) => {
  if (!attachment) return null;
  const Icon = iconForFile(attachment.fileType);
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        maxWidth: '100%',
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${THEME.border}`,
        borderRadius: '6px',
        px: 1,
        py: 0.5,
      }}
    >
      <Icon sx={{ fontSize: 14, color: THEME.textMuted, flexShrink: 0 }} />
      <Typography
        sx={{
          fontSize: '12px',
          color: THEME.textPrimary,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {attachment.filename}
      </Typography>
      <Typography sx={{ fontSize: '11px', color: THEME.textFaint, flexShrink: 0 }}>
        {formatBytes(attachment.sizeBytes)}
      </Typography>
      {onDelete && (
        <Box
          component="button"
          onClick={onDelete}
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: THEME.textMuted,
            p: 0,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            '&:hover': { color: THEME.pink },
          }}
        >
          <CloseIcon sx={{ fontSize: 12 }} />
        </Box>
      )}
    </Box>
  );
};
